import React from "react";
import useLogin from "@common/components/hooks/useLogin";
import LoginAction from "@common/redux/actions/LoginAction";
import { Controller } from "react-hook-form";
import { Dropdown, Form, Icon, Label } from "semantic-ui-react";
import debounce from "lodash.debounce";
import { useSelector } from "react-redux";

/**
 * SelectComponent renders a select field with validation.
 *
 * @typedef {object} SelectProps
 * @property {string} name - Name of the select field.
 * @property {string} [label=''] - Label of the select field.
 * @property {string} placeholder - Placeholder text of the select field.
 * @property {Function} control - Function to control the select field for form
 * @property {Object} [error=null] - Object containing validation error.
 * @property {array} options - Options for the select component.
 * @property {boolean} [loading=false] - Show that it is curently loading data.
 * @property {boolean} [allowAdditions=false] - Allow additions.
 * @property {Object} [additionLabel=null] - React element for addition button.
 * @property {Function} [handleAddition=()=>{}] - Function when clicking addition button.
 * @property {Function} [handleSearch=()=>{}] - Function to call on search.
 * @property {Function} [handleFocus=()=>{}] - Function to call on focus.
 * @property {Function} [onType=()=>{}] - Function to capture search input on type.
 * @property {Object} [noResultsMessage=.No result found] - Object for no results message label.
 * @property {Function} [onSelect=()=>{}] - Function to call on select.
 * @property {string} [labelStyle='w-30'] - Style for the label.
 * @property {string} [fieldStyle='flex-between flex-row-center'] - Style for Form.Field.
 * @property {string} [selectContainerStyle='w-100'] - Style for select container.
 *
 * @component
 * @param {SelectProps} props - The component props.
 * @returns {JSX.Element} The rendered select form component with validation.
 */
export default function SearchableDropdownComponent({
  name,
  label = "",
  placeholder,
  control,
  error = null,
  options,
  loading = false,
  allowAdditions = false,
  additionButton = null,
  handleAddition = () => {},
  handleSearch = () => {},
  handleFocus = () => {},
  onType = () => {},
  noResultsMessage = "No result found",
  onSelect = () => {},
  labelStyle = "w-30",
  fieldStyle = "flex-between flex-row-center",
  selectContainerStyle = "w-100",
}) {
  const { siteGuid } = useSelector((state) => LoginAction.getStatePath(state));

  const optionsMap = options.map((option) => {
    const features = [
      option.gender?.name,
      `${option.ageRange?.name} years`,
      `${option.height?.name} height`,
      `${option.build?.name} build`,
      `${option.hair?.name} hair`,
      option.ethnicity?.name,
    ];

    const isPrimary = option.site?.guid === siteGuid;
    return {
      ...option,
      content: (
        <div>
          <strong>
            {" "}
            {isPrimary && <Icon name="star" color="yellow" />}
            {option.text}
          </strong>

          <div className="gray-content mt-0_5">
            <Label.Group>
              <strong>{!isPrimary && option.site?.name}</strong>{" "}
              {features.join(", ")}
            </Label.Group>
          </div>
        </div>
      ),
    };
  });

  // Debounce the handleSearch function
  const debouncedHandleSearch = debounce(handleSearch, 300);

  const [{ isInReadMode }] = useLogin();

  if (isInReadMode) {
    return <></>;
  }

  return (
    <Form.Field className={fieldStyle} error={!!error}>
      {label && <label className={labelStyle}>{label}</label>}
      <div className={selectContainerStyle}>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => {
            return (
              <div className="flex-center-0_25">
                <Dropdown
                  scrolling
                  fluid
                  placeholder={placeholder}
                  options={optionsMap}
                  loading={loading}
                  value={value}
                  search
                  selection
                  selectOnNavigation={false}
                  selectOnBlur={false}
                  defaultValue={null}
                  onFocus={handleFocus}
                  onChange={(_, { value }) => {
                    onChange(value);
                    onSelect(value);
                  }}
                  noResultsMessage={noResultsMessage}
                  onAddItem={handleAddition}
                  onSearchChange={(_, { searchQuery }) => {
                    if (searchQuery.length >= 3) {
                      onType(searchQuery);
                      debouncedHandleSearch(searchQuery);
                    }
                  }}
                />
                {allowAdditions && additionButton}
              </div>
            );
          }}
        />
        {error && (
          <Label pointing="above" prompt className="m-0">
            {error?.message}
          </Label>
        )}
      </div>
    </Form.Field>
  );
}
